import {
	multiplySize,
	type GenericPosition,
	type GenericSize,
} from "@brickme/project-core/src/model/geom.ts";
import type { Bitmap } from "@brickme/project-core/src/bitmap/types.ts";
import { bitmapToImageData } from "@brickme/project-core/src/bitmap/image-data.ts";
import { renderPictureSync } from "@brickme/project-core/src/render/render-picture.ts";
import { runGeneratorSync } from "@brickme/project-core/src/utils/run-generator.ts";
import {
	realWorldBrickSize,
	type PictureConfiguration,
} from "@brickme/project-core/src/model/picture.ts";
import renderPictureAsPixels from "@brickme/project-core/src/render/render-picture-as-pixels.ts";
import renderBaseplateOutlines from "@brickme/project-core/src/render/render-baseplate-outlines.ts";
import type {
	RenderRunner,
	RenderSource,
	LoadedScene,
} from "./render-runner-type.ts";
import { ensureExhaustive } from "../utils/never.ts";
import renderSource from "./render-source.ts";

// TODO: Find a better system for preventing unnecessary re-renders.
type CurrentRender = {
	readonly type: RenderSource["type"];
	// readonly drawBaseplates: boolean;
	// readonly picture: PictureConfiguration | undefined;
	// readonly build: BuildBitmap | undefined;
	// Not aborting any more
	// readonly abortController: AbortController;
};

type Options = {
	// In main thread will want timeout. But slower, so in worker prefer microtask
	readonly pendingRenderMethod: "timeout" | "microtask";
};

function createRenderRunner(
	_: Options,
): RenderRunner<HTMLCanvasElement | OffscreenCanvas> {
	let performingRender: CurrentRender | undefined;
	let canvasCtx:
		| OffscreenCanvasRenderingContext2D
		| CanvasRenderingContext2D
		| undefined;
	let utilityCanvasCtx: Parameters<typeof renderPictureSync>[2] | undefined;
	let canvasSourceImage: HTMLCanvasElement | OffscreenCanvas | undefined;
	let source: RenderSource | undefined;
	let canvasSize: GenericSize<number> | undefined;
	let loadedScene: LoadedScene | undefined;
	let hasPendingRender = false;
	let drawBaseplates = false;
	// TODO: Rendering twice. Once on picture change, even though some render configurations
	// don't require it.
	let picture: PictureConfiguration | undefined;

	function tryRender() {
		if (
			!canvasCtx ||
			!utilityCanvasCtx ||
			!source ||
			!canvasSize ||
			!canvasSourceImage ||
			!picture
		) {
			return;
		}

		// We let any current render continue to give quicker user feedback.
		// then pick up pending renders later. This solves the case of say scrubbing a slider.
		// If the slider emits an event every 10ms and render takes 15ms then we'll never see
		// a render until the user stops moving the slider.
		// currentRender.abortController.abort();
		if (performingRender) {
			hasPendingRender = true;
			return;
		}

		const myRender = {
			type: source.type,
			// drawBaseplates,
			// build: source.type === "bricked" ? source.build : undefined,
			// picture: drawBaseplates || source.type === "source" ? picture : undefined
		};
		performingRender = myRender;
		// Setting width or height (even if the same) results in a canvas clear
		if (canvasCtx.canvas.width !== canvasSize.width) {
			canvasCtx.canvas.width = canvasSize.width;
		}
		if (canvasCtx.canvas.height !== canvasSize.height) {
			canvasCtx.canvas.height = canvasSize.height;
		}

		if (import.meta.env.MODE === "development") {
			// eslint-disable-next-line no-console
			console.time(`Render (${myRender.type})`);
		}

		// Positioning
		let drawBounds: (GenericSize<number> & GenericPosition<number>) | undefined;
		if (loadedScene) {
			// Positioning
			const realWorldPictureSizeMms = multiplySize(
				{
					width: picture.basePlateSize * picture.numberOfBasePlates.width,
					height: picture.basePlateSize * picture.numberOfBasePlates.height,
				},
				realWorldBrickSize.mm,
			);
			const destinationWidth =
				(canvasSize.width * realWorldPictureSizeMms.width) /
				loadedScene.scene.fullSize.realWorldSize.width.mm;
			const destinationHeight =
				(canvasSize.height * realWorldPictureSizeMms.height) /
				loadedScene.scene.fullSize.realWorldSize.height.mm;
			drawBounds = {
				x: Math.round(
					loadedScene.scene.fullSize.mountBoundsRatios.centrePosition.x *
						canvasSize.width -
						destinationWidth / 2,
				),
				y: Math.round(
					loadedScene.scene.fullSize.mountBoundsRatios.centrePosition.y *
						canvasSize.height -
						destinationHeight / 2,
				),
				width: Math.max(1, Math.round(destinationWidth)),
				height: Math.max(1, Math.round(destinationHeight)),
			};

			// Draw scene
			if (loadedScene.loadedImage) {
				canvasCtx.drawImage(
					loadedScene.loadedImage,
					0,
					0,
					canvasSize.width,
					canvasSize.height,
				);
			} else {
				canvasCtx.clearRect(0, 0, canvasSize.width, canvasSize.height);
			}

			// Draw a shadow
			const shadowOffset = 0.8;
			canvasCtx.fillStyle = "rgba(0, 0, 0, 0.5)";
			canvasCtx.fillRect(
				drawBounds.x + shadowOffset,
				drawBounds.y + shadowOffset,
				drawBounds.width + shadowOffset,
				drawBounds.height + shadowOffset,
			);
		}

		// Draw main image
		switch (source.type) {
			case "bricked":
				renderPictureSync(canvasCtx, source.build, utilityCanvasCtx, {
					drawBounds,
				});
				break;
			case "solid":
				runGeneratorSync(
					renderPictureAsPixels(canvasCtx, utilityCanvasCtx, source.build),
				);
				break;
			case "source":
				renderSource(canvasCtx, canvasSourceImage, picture);
				break;
			default:
				ensureExhaustive(source);
		}

		// Draw baseplates
		if (drawBaseplates) {
			renderBaseplateOutlines(
				canvasCtx,
				picture.numberOfBasePlates,
				canvasCtx.canvas.width,
				canvasCtx.canvas.height,
			);
		}

		if (import.meta.env.MODE === "development") {
			// if (myRender.abortController.signal.aborted) {
			// 	console.log("Rendering ABORTED");
			// } else
			// eslint-disable-next-line no-console
			console.timeEnd(`Render (${myRender.type})`);
		}

		if (performingRender === myRender) {
			performingRender = undefined;
		}

		if (hasPendingRender) {
			hasPendingRender = false;
			// Use next frame instead of direct call to avoid stack overflows
			tryRenderNextFrame();
		}
	}

	let nextFrameRenderId: ReturnType<typeof setTimeout> | undefined;
	function tryRenderNextFrame() {
		// queueMicrotask happens too quick. No time to cancel before redundant call comes in.
		// This is part of multiple safeguards to try and prevent too many renders.
		// if (pendingRenderMethod === "microtask") {
		// queueMicrotask(() => void tryRender());
		// } else {
		if (nextFrameRenderId !== undefined) {
			clearTimeout(nextFrameRenderId);
		}
		nextFrameRenderId = setTimeout(tryRender);
	}

	return {
		setSourceImage(
			sourceImage: Bitmap,
			utilityCanvas: HTMLCanvasElement | OffscreenCanvas,
		) {
			/* eslint-disable no-param-reassign */
			utilityCanvas.width = sourceImage.width;
			utilityCanvas.height = sourceImage.height;
			/* eslint-enable no-param-reassign */
			canvasSourceImage = utilityCanvas;
			const sourceImageCtx = utilityCanvas.getContext("2d", {
				alpha: false,
			});
			if (sourceImageCtx === null) {
				throw new Error("Failed to get context");
			}
			sourceImageCtx.putImageData(bitmapToImageData(sourceImage), 0, 0);
			tryRenderNextFrame();
		},
		setCanvas(newCanvas: HTMLCanvasElement | OffscreenCanvas) {
			const newCtx = newCanvas.getContext("2d", {
				alpha: false,
			});
			if (newCtx === null) {
				throw new Error("Canvas ctx not working");
			}
			canvasCtx = newCtx;
			tryRenderNextFrame();
		},
		setSource(newSource: RenderSource | undefined) {
			source = newSource;
			tryRenderNextFrame();
		},
		setDrawBaseplates(newDrawBaseplates: boolean) {
			if (newDrawBaseplates !== drawBaseplates) {
				drawBaseplates = newDrawBaseplates;
				tryRenderNextFrame();
			}
		},
		setLoadedScene(newLoadedScene: LoadedScene) {
			if (newLoadedScene !== loadedScene) {
				loadedScene = newLoadedScene;
				tryRenderNextFrame();
			}
		},
		setPictureConfiguration(newPicture: PictureConfiguration) {
			picture = newPicture;
			// Only source renders or base plates need picture config
			if (source?.type === "source" || drawBaseplates) {
				tryRenderNextFrame();
			}
		},
		setUtilityCanvas(
			newUtilityCanvas: HTMLCanvasElement | OffscreenCanvas,
			newCanvasSize: GenericSize<number>,
		) {
			canvasSize = newCanvasSize;
			const newCtx = newUtilityCanvas.getContext("2d", {
				alpha: false,
			});
			if (newCtx === null) {
				throw new Error("Utility ctx not working");
			}
			utilityCanvasCtx = newCtx;
			tryRenderNextFrame();
		},
		clearUtilityCanvas() {
			canvasSize = undefined;
			utilityCanvasCtx = undefined;
			tryRenderNextFrame();
		},
		terminate() {
			// Nothing
		},
	};
}

export { createRenderRunner };
